import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import ClientSidenav from '../components/ClientSidenav';
import './PlanoEstudos.css';

const DAYS_OF_WEEK = [
  { key: 'monday',    label: 'Segunda', offset: 0 },
  { key: 'tuesday',   label: 'Terça',   offset: 1 },
  { key: 'wednesday', label: 'Quarta',  offset: 2 },
  { key: 'thursday',  label: 'Quinta',  offset: 3 },
  { key: 'friday',    label: 'Sexta',   offset: 4 },
  { key: 'saturday',  label: 'Sábado',  offset: 5 },
  { key: 'sunday',    label: 'Domingo', offset: 6 },
];

const GANTT_COLORS = ['#667eea','#f093fb','#4facfe','#43e97b','#fa709a'];
const GANTT_COLORS_DARK = ['#764ba2','#f5576c','#00f2fe','#38f9d7','#fee140'];

function getMonday(date) {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  return new Date(d.setDate(diff));
}

function formatDate(date) { return date.toISOString().split('T')[0]; }

function parseHours(duration) {
  if (!duration) return 0;
  const h = duration.match(/(\d+)h/);
  const m = duration.match(/(\d+)min/);
  return (h ? parseInt(h[1]) : 0) + (m ? parseInt(m[1]) / 60 : 0);
}

function formatHours(hours) {
  const h = Math.floor(hours);
  const m = Math.round((hours - h) * 60);
  return m === 0 ? `${h}h` : `${h}h ${m}min`;
}

function PlanoEstudos() {
  const navigate = useNavigate();
  const [courses, setCourses] = useState([]);
  const [weekPlan, setWeekPlan] = useState({});
  const [draggedCourse, setDraggedCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentWeekStart, setCurrentWeekStart] = useState(getMonday(new Date()));
  const [showModal, setShowModal] = useState(false);
  const [modalData, setModalData] = useState({ course: null, date: null, day: null });
  const [studyHours, setStudyHours] = useState('');
  const [studyMinutes, setStudyMinutes] = useState('');
  const user = JSON.parse(localStorage.getItem('user_data') || '{}');

  useEffect(() => {
    if (!user.email) { navigate('/treinamentos'); return; }
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [enrollmentsRes, coursesRes] = await Promise.all([
        api.get('/enrollments', true),
        api.get('/courses')
      ]);
      if (enrollmentsRes.success && coursesRes.success) {
        const enrolledCourses = enrollmentsRes.data.map(enrollment => {
          const course = coursesRes.data.find(c => c.id === enrollment.courseId);
          return { ...enrollment, ...course };
        });
        setCourses(enrolledCourses);
      }
      const savedPlan = localStorage.getItem(`study_plan_${user.email}`);
      if (savedPlan) setWeekPlan(JSON.parse(savedPlan));
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getDateForDay = (dayOffset) => {
    const date = new Date(currentWeekStart);
    date.setDate(date.getDate() + dayOffset);
    return date;
  };

  const isPastDate = (date) => {
    const today = new Date(); today.setHours(0,0,0,0);
    const check = new Date(date); check.setHours(0,0,0,0);
    return check < today;
  };

  const getTotalPlannedHours = (courseId) => {
    let total = 0;
    Object.values(weekPlan).forEach(dayPlan =>
      dayPlan.forEach(item => { if (item.id === courseId) total += item.studyHours || 0; })
    );
    return total;
  };

  const getTotalRemainingHours = (course) => {
    const totalHours = parseHours(course.duration);
    const completedHours = (totalHours * (course.progress || 0)) / 100;
    return Math.max(0, totalHours - completedHours);
  };

  const calculateRemainingHours = (course) => {
    if (!course) return 0;
    const totalHours = parseHours(course.duration);
    const completedHours = (totalHours * (course.progress || 0)) / 100;
    return Math.max(0, totalHours - completedHours - getTotalPlannedHours(course.id));
  };

  const handleDragStart = (e, course, fromDate = null) => {
    setDraggedCourse({ ...course, fromDate });
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; };

  const handleDrop = (e, dayOffset) => {
    e.preventDefault();
    if (!draggedCourse) return;
    const date = getDateForDay(dayOffset);
    const dateKey = formatDate(date);
    if (isPastDate(date)) {
      alert('Não é possível planejar estudos para datas passadas!');
      setDraggedCourse(null);
      return;
    }
    if (draggedCourse.fromDate) {
      const newPlan = { ...weekPlan };
      if (newPlan[draggedCourse.fromDate]) {
        newPlan[draggedCourse.fromDate] = newPlan[draggedCourse.fromDate].filter(
          item => !(item.id === draggedCourse.id && item.date === draggedCourse.fromDate)
        );
      }
      setWeekPlan(newPlan);
      localStorage.setItem(`study_plan_${user.email}`, JSON.stringify(newPlan));
      setDraggedCourse(null);
      return;
    }
    setModalData({ course: draggedCourse, date: dateKey, day: DAYS_OF_WEEK[dayOffset].label });
    setShowModal(true);
  };

  const handleDropToSidebar = (e) => {
    e.preventDefault();
    if (!draggedCourse || !draggedCourse.fromDate) return;
    const newPlan = { ...weekPlan };
    if (newPlan[draggedCourse.fromDate]) {
      newPlan[draggedCourse.fromDate] = newPlan[draggedCourse.fromDate].filter(
        item => !(item.id === draggedCourse.id && item.date === draggedCourse.fromDate)
      );
    }
    setWeekPlan(newPlan);
    localStorage.setItem(`study_plan_${user.email}`, JSON.stringify(newPlan));
    setDraggedCourse(null);
  };

  const confirmAddCourse = () => {
    const hours = parseInt(studyHours) || 0;
    const minutes = parseInt(studyMinutes) || 0;
    const totalHours = hours + minutes / 60;
    if (totalHours <= 0) { alert('Informe pelo menos 1 minuto'); return; }
    const newPlan = { ...weekPlan };
    if (!newPlan[modalData.date]) newPlan[modalData.date] = [];
    newPlan[modalData.date].push({ ...modalData.course, studyHours: totalHours, date: modalData.date });
    setWeekPlan(newPlan);
    localStorage.setItem(`study_plan_${user.email}`, JSON.stringify(newPlan));
    setShowModal(false);
    setStudyHours('');
    setStudyMinutes('');
    setModalData({ course: null, date: null, day: null });
    setDraggedCourse(null);
  };

  const clearPlan = () => {
    if (window.confirm('Deseja limpar todo o plano de estudos?')) {
      setWeekPlan({});
      localStorage.removeItem(`study_plan_${user.email}`);
    }
  };

  const generateGanttData = () => {
    const coursesTimeline = {};
    Object.keys(weekPlan).sort().forEach(dateKey => {
      weekPlan[dateKey].forEach(item => {
        if (!coursesTimeline[item.id]) {
          coursesTimeline[item.id] = { course: item, dates: [], totalHours: 0, sessions: [], daysWithSessions: new Set() };
        }
        coursesTimeline[item.id].dates.push(dateKey);
        coursesTimeline[item.id].totalHours += item.studyHours || 0;
        coursesTimeline[item.id].daysWithSessions.add(dateKey);
        coursesTimeline[item.id].sessions.push({ date: dateKey, hours: item.studyHours || 0 });
      });
    });
    return Object.values(coursesTimeline).map(timeline => {
      const sorted = timeline.dates.sort();
      return { ...timeline, startDate: sorted[0], endDate: sorted[sorted.length - 1], actualDays: timeline.daysWithSessions.size };
    });
  };

  if (loading) {
    return (
      <div className="dark-loading">
        <div className="dark-spinner"/>
        <p>Carregando...</p>
      </div>
    );
  }

  const months = ['jan','fev','mar','abr','mai','jun','jul','ago','set','out','nov','dez'];

  return (
    <div className="plano-estudos">
      <ClientSidenav />

      <div className="plano-content">
        {/* Page header */}
        <div className="plano-page-header">
          <div>
            <h1>Plano de Estudos</h1>
            <p className="plano-page-subtitle">
              Arraste seus cursos e defina quantas horas dedicará em cada dia
            </p>
          </div>
          <button onClick={clearPlan} className="btn-clear-plan">
            Limpar Plano
          </button>
        </div>

        {/* Week navigation */}
        <div className="week-navigation">
          <button onClick={() => { const d = new Date(currentWeekStart); d.setDate(d.getDate()-7); setCurrentWeekStart(d); }} className="btn-week-nav">
            ← Semana Anterior
          </button>
          <button onClick={() => setCurrentWeekStart(getMonday(new Date()))} className="btn-week-current">
            Semana Atual
          </button>
          <button onClick={() => { const d = new Date(currentWeekStart); d.setDate(d.getDate()+7); setCurrentWeekStart(d); }} className="btn-week-nav">
            Próxima Semana →
          </button>
          <span className="week-range">
            {currentWeekStart.toLocaleDateString('pt-BR')} — {getDateForDay(6).toLocaleDateString('pt-BR')}
          </span>
        </div>

        {/* Planner grid */}
        <div className="plano-grid">
          {/* Sidebar */}
          <div className="courses-sidebar" onDragOver={handleDragOver} onDrop={handleDropToSidebar}>
            <h3>Meus Cursos</h3>
            <div className="courses-list">
              {courses.length === 0 ? (
                <div className="empty-sidebar-courses">
                  <p>Você ainda não tem cursos</p>
                  <button className="btn-sidebar-catalog" onClick={() => navigate('/treinamentos/cursos')}>
                    Ver Catálogo
                  </button>
                </div>
              ) : (
                courses.map(course => (
                  <div
                    key={course.id}
                    className="course-item"
                    draggable
                    onDragStart={(e) => handleDragStart(e, course)}
                  >
                    {course.coverImage && <img src={course.coverImage} alt={course.title}/>}
                    <div className="course-item-info">
                      <h4>{course.title}</h4>
                      <div className="course-meta">
                        <span className="course-progress">{course.progress || 0}%</span>
                        <span>📋 {formatHours(getTotalPlannedHours(course.id))} planejadas</span>
                        <span>⏱️ {formatHours(getTotalRemainingHours(course))} restam</span>
                      </div>
                    </div>
                    <button
                      className="btn-go-course"
                      onClick={(e) => { e.stopPropagation(); navigate(`/treinamentos/curso/${course.id}`); }}
                      title="Ir para o curso"
                    >▶</button>
                    <div className="drag-icon">⋮⋮</div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Week calendar */}
          <div className="week-calendar">
            {DAYS_OF_WEEK.map(day => {
              const dayDate = getDateForDay(day.offset);
              const dateKey = formatDate(dayDate);
              const dayPlan = weekPlan[dateKey] || [];
              const isPast = isPastDate(dayDate);
              const isWeekend = day.key === 'saturday' || day.key === 'sunday';

              return (
                <div
                  key={day.key}
                  className={`day-column${isPast ? ' day-past' : ''}${isWeekend ? ' day-weekend' : ''}`}
                  onDragOver={handleDragOver}
                  onDrop={(e) => handleDrop(e, day.offset)}
                >
                  <div className="day-header">
                    <h3>{day.label}</h3>
                    <span className="day-date">{dayDate.getDate()}/{dayDate.getMonth()+1}</span>
                    {isPast && <span className="past-label">Passado</span>}
                    <span className="day-count">{dayPlan.length} {dayPlan.length === 1 ? 'curso' : 'cursos'}</span>
                  </div>
                  <div className="day-content">
                    {dayPlan.length > 0 ? (
                      dayPlan.map((item, idx) => (
                        <div
                          key={`${item.id}-${idx}`}
                          className="planned-course"
                          draggable
                          onDragStart={(e) => handleDragStart(e, item, dateKey)}
                        >
                          {item.coverImage && <img src={item.coverImage} alt={item.title}/>}
                          <div className="planned-course-info">
                            <h4>{item.title}</h4>
                            <span className="hours-badge">⏱️ {formatHours(item.studyHours)}</span>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="empty-day"><p>Arraste cursos aqui</p></div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Gantt chart */}
        {Object.keys(weekPlan).length > 0 && (
          <div className="gantt-section">
            <div className="gantt-section-header">
              <h2>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <rect x="3" y="4" width="8" height="3" rx="1.5" fill="#667eea"/>
                  <rect x="3" y="10" width="14" height="3" rx="1.5" fill="#f093fb"/>
                  <rect x="3" y="16" width="11" height="3" rx="1.5" fill="#4facfe"/>
                </svg>
                Cronograma de Estudos
              </h2>
              <p>Acompanhe sua jornada de aprendizado ao longo do tempo</p>
            </div>
            <div className="gantt-chart">
              {generateGanttData().map((timeline, idx) => {
                const [, sM, sD] = timeline.startDate.split('-').map(Number);
                const [, eM, eD] = timeline.endDate.split('-').map(Number);
                return (
                  <div key={timeline.course.id} className="gantt-row">
                    <div className="gantt-course-info">
                      {timeline.course.coverImage && (
                        <img src={timeline.course.coverImage} alt={timeline.course.title}/>
                      )}
                      <div>
                        <h4>{timeline.course.title}</h4>
                        <span className="gantt-meta">
                          {formatHours(timeline.totalHours)} • {timeline.sessions.length} sessões
                        </span>
                      </div>
                    </div>
                    <div className="gantt-timeline">
                      <div
                        className="gantt-bar"
                        style={{ background: `linear-gradient(135deg, ${GANTT_COLORS[idx % 5]} 0%, ${GANTT_COLORS_DARK[idx % 5]} 100%)` }}
                      >
                        <span className="gantt-dates">
                          {sD}/{months[sM-1]} → {eD}/{months[eM-1]}
                        </span>
                        <span className="gantt-duration">
                          {timeline.actualDays} {timeline.actualDays === 1 ? 'dia' : 'dias'}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>Tempo de Estudo</h2>
            <p className="modal-course-name">{modalData.course?.title}</p>
            <p className="modal-day">
              {modalData.day} — {modalData.date ? new Date(modalData.date + 'T12:00').toLocaleDateString('pt-BR') : ''}
            </p>
            <div className="modal-info">
              <p>Progresso atual: <strong>{modalData.course?.progress || 0}%</strong></p>
              <p>Horas restantes: <strong>{formatHours(calculateRemainingHours(modalData.course))}</strong></p>
            </div>
            <div className="modal-input">
              <label>Quanto tempo você dedicará?</label>
              <div className="time-inputs">
                <div className="time-field">
                  <input
                    type="number" min="0" max="23"
                    value={studyHours}
                    onChange={(e) => setStudyHours(e.target.value)}
                    placeholder="0" autoFocus
                  />
                  <span className="time-label">horas</span>
                </div>
                <div className="time-field">
                  <input
                    type="number" min="0" max="59" step="15"
                    value={studyMinutes}
                    onChange={(e) => setStudyMinutes(e.target.value)}
                    placeholder="0"
                  />
                  <span className="time-label">minutos</span>
                </div>
              </div>
            </div>
            <div className="modal-actions">
              <button onClick={() => setShowModal(false)} className="btn-modal-cancel">Cancelar</button>
              <button onClick={confirmAddCourse} className="btn-modal-confirm">Confirmar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default PlanoEstudos;
